import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

const Navbar = () => {
  const { t, i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  const isHome = location.pathname === "/";

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const switchLang = () => {
    i18n.changeLanguage(i18n.language === "en" ? "es" : "en");
  };

  const navLinks = [
    { label: t("nav_home"), path: "/" },
    { label: t("nav_services"), path: "/services" },
    { label: t("nav_about"), path: "/about" },
    { label: t("nav_pricing"), path: "/pricing" },
    { label: t("nav_reviews"), path: "/reviews" },
    { label: t("nav_faq"), path: "/faq" },
    { label: t("nav_contact"), path: "/contact" },
  ];

  const navBg =
    scrolled || mobileOpen || !isHome
      ? "bg-[#0F2A44] shadow-lg"
      : "bg-transparent";

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${navBg}`}
      style={{ fontFamily: "'Poppins', sans-serif" }}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-20">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 shrink-0 group">
          <div
            className="flex items-center justify-center px-4 py-2 rounded-2xl bg-[#D4AF37] transition-all duration-300 group-hover:bg-[#B8952E] group-hover:scale-105"
            style={{
              animation: "logoFadeIn 0.6s cubic-bezier(0.22,1,0.36,1) both",
              boxShadow: "0 0 0 0 rgba(212,175,55,0.5)",
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLDivElement).style.boxShadow =
                "0 0 18px 6px rgba(212,175,55,0.45)";
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLDivElement).style.boxShadow =
                "0 0 0 0 rgba(212,175,55,0.5)";
            }}
          >
            <img
              src="https://storage.readdy-site.link/project_files/a749ed34-0552-42ad-80d0-bc014c702bfa/ddb227b9-44ac-4d46-8046-3ebd50701fa1_49D95226-89DE-44DF-B084-CFB8EB8D11D6.png?v=a9bb350f072ed3787796e06870b2d213"
              alt="Prime Shine WA Logo"
              className="h-11 w-auto object-contain"
            />
          </div>
        </Link>

        {/* Desktop Nav */}
        <ul className="hidden lg:flex items-center gap-1">
          {navLinks.map((link) => {
            const isActive = location.pathname === link.path;
            return (
              <li key={link.label}>
                <Link
                  to={link.path}
                  className={`px-3 py-2 text-sm font-medium transition-colors duration-200 whitespace-nowrap
                    ${isActive ? "text-[#D4AF37]" : "text-white/90 hover:text-[#D4AF37]"}`}
                >
                  {link.label}
                </Link>
              </li>
            );
          })}
        </ul>

        {/* Right Actions */}
        <div className="hidden lg:flex items-center gap-3">
          <button
            onClick={switchLang}
            className="text-xs font-semibold text-white/70 hover:text-[#D4AF37] border border-white/30 rounded-full px-3 py-1.5 transition-all duration-200 whitespace-nowrap cursor-pointer"
          >
            {i18n.language === "en" ? "ES" : "EN"}
          </button>
          <Link
            to="/contact"
            className="bg-[#D4AF37] hover:bg-[#B8952E] text-[#0F2A44] font-semibold text-sm px-5 py-2.5 rounded-full transition-all duration-200 hover:scale-105 whitespace-nowrap cursor-pointer"
          >
            {t("nav_cta")}
          </Link>
        </div>

        {/* Mobile Hamburger */}
        <div className="flex lg:hidden items-center gap-3">
          <button
            onClick={switchLang}
            className="text-xs font-semibold text-white/70 border border-white/30 rounded-full px-3 py-1 whitespace-nowrap cursor-pointer"
          >
            {i18n.language === "en" ? "ES" : "EN"}
          </button>
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="text-white w-8 h-8 flex items-center justify-center cursor-pointer"
          >
            <i className={`ri-${mobileOpen ? "close" : "menu"}-line text-2xl`} />
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-[#0F2A44] border-t border-white/10 px-6 py-4">
          {navLinks.map((link) => (
            <Link
              key={link.label}
              to={link.path}
              onClick={() => setMobileOpen(false)}
              className="block w-full text-left py-3 text-white/90 hover:text-[#D4AF37] font-medium text-sm border-b border-white/10"
            >
              {link.label}
            </Link>
          ))}
          <Link
            to="/contact"
            onClick={() => setMobileOpen(false)}
            className="mt-4 block w-full bg-[#D4AF37] text-[#0F2A44] font-semibold py-3 rounded-full text-sm text-center whitespace-nowrap"
          >
            {t("nav_cta")}
          </Link>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
